import * as THREE from 'three';

/**
 * Visual effects: hit sparks, shield blocks, dust, slash trails
 */
// Shared geometries (avoid per-particle allocation)
const _sphereGeoS = new THREE.SphereGeometry(0.1, 3, 3);
const _sphereGeoM = new THREE.SphereGeometry(0.25, 3, 3);
const _planeGeoS = new THREE.PlaneGeometry(0.3, 0.06);
const _planeGeoM = new THREE.PlaneGeometry(0.25, 0.25);

export class Effects {
  constructor(scene) {
    this.scene = scene;
    this.particles = [];
  }

  update(dt) {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;

      if (p.vel) {
        p.mesh.position.addScaledVector(p.vel, dt);
        p.vel.y -= 10 * dt; // Gravity
      }

      if (p.scale) {
        p.mesh.scale.multiplyScalar(p.scale);
      }

      p.mesh.material.opacity = Math.max(0, p.life / p.maxLife);

      if (p.spin) {
        p.mesh.rotation.x += p.spin * dt;
        p.mesh.rotation.z += p.spin * dt;
      }

      if (p.life <= 0) {
        this.scene.remove(p.mesh);
        // Don't dispose shared geometry
        p.mesh.material.dispose();
        this.particles.splice(i, 1);
      }
    }
  }

  /** Orange sparks when rock hits */
  rockHitEffect(position) {
    for (let i = 0; i < 10; i++) {
      const geo = _sphereGeoS;
      const mat = new THREE.MeshBasicMaterial({
        color: new THREE.Color().setHSL(0.06, 1, 0.5 + Math.random() * 0.3),
        transparent: true,
        opacity: 1,
      });
      const m = new THREE.Mesh(geo, mat);
      m.position.copy(position);
      this.scene.add(m);
      const life = 0.3 + Math.random() * 0.5;
      this.particles.push({
        mesh: m,
        life,
        maxLife: life,
        vel: new THREE.Vector3(
          (Math.random() - 0.5) * 12,
          Math.random() * 8,
          (Math.random() - 0.5) * 12
        ),
        scale: 0.97,
      });
    }
    // Flash
    this._flash(position, 0xFF6600, 3, 0.2);
  }

  /** Blue/white flash when shield blocks rock */
  shieldBlockEffect(position) {
    for (let i = 0; i < 8; i++) {
      const geo = _planeGeoS;
      const mat = new THREE.MeshBasicMaterial({
        color: 0x88CCFF,
        transparent: true,
        opacity: 1,
        side: THREE.DoubleSide,
      });
      const m = new THREE.Mesh(geo, mat);
      m.position.copy(position);
      this.scene.add(m);
      const life = 0.2 + Math.random() * 0.3;
      this.particles.push({
        mesh: m,
        life,
        maxLife: life,
        vel: new THREE.Vector3(
          (Math.random() - 0.5) * 6,
          Math.random() * 4,
          (Math.random() - 0.5) * 6
        ),
        spin: 10 + Math.random() * 10,
        scale: 0.95,
      });
    }
    this._flash(position, 0x88CCFF, 5, 0.3);
  }

  /** Red slash trail for scissors */
  scissorsSlashEffect(origin, direction) {
    const perpendicular = new THREE.Vector3(-direction.z, 0, direction.x);
    for (let i = 0; i < 8; i++) {
      const t = (i / 8 - 0.5) * 3;
      const geo = new THREE.PlaneGeometry(0.4, 0.1);
      const mat = new THREE.MeshBasicMaterial({
        color: 0xFF4444,
        transparent: true,
        opacity: 0.8,
        side: THREE.DoubleSide,
      });
      const m = new THREE.Mesh(geo, mat);
      m.position.copy(origin).addScaledVector(perpendicular, t)
        .add(new THREE.Vector3(0, 1.5, 0))
        .addScaledVector(direction, 2);
      m.rotation.y = Math.atan2(direction.x, direction.z);
      this.scene.add(m);
      const life = 0.15 + Math.random() * 0.2;
      this.particles.push({
        mesh: m,
        life,
        maxLife: life,
        scale: 0.92,
        spin: 5,
      });
    }
  }

  /** Scissors hits paper - extra dramatic effect */
  scissorsVsPaperEffect(position) {
    // Paper shreds
    for (let i = 0; i < 12; i++) {
      const geo = _planeGeoM;
      const mat = new THREE.MeshBasicMaterial({
        color: 0xFFF8E7,
        transparent: true,
        opacity: 1,
        side: THREE.DoubleSide,
      });
      const m = new THREE.Mesh(geo, mat);
      m.position.copy(position).add(new THREE.Vector3(0, 1.5, 0));
      this.scene.add(m);
      const life = 0.5 + Math.random() * 0.8;
      this.particles.push({
        mesh: m,
        life,
        maxLife: life,
        vel: new THREE.Vector3(
          (Math.random() - 0.5) * 8,
          Math.random() * 6,
          (Math.random() - 0.5) * 8
        ),
        spin: 8 + Math.random() * 8,
        scale: 0.98,
      });
    }
    this._flash(position, 0xFF2200, 4, 0.3);
  }

  /** Dust puff when player lands or gets knocked back */
  dustPuff(position) {
    for (let i = 0; i < 5; i++) {
      const geo = _sphereGeoM;
      const mat = new THREE.MeshBasicMaterial({
        color: 0xd4a574,
        transparent: true,
        opacity: 0.5,
      });
      const m = new THREE.Mesh(geo, mat);
      m.position.copy(position).add(new THREE.Vector3(0, 0.2, 0));
      this.scene.add(m);
      const life = 0.4 + Math.random() * 0.3;
      this.particles.push({
        mesh: m,
        life,
        maxLife: life,
        vel: new THREE.Vector3(
          (Math.random() - 0.5) * 4,
          Math.random() * 2,
          (Math.random() - 0.5) * 4
        ),
        scale: 1.02,
      });
    }
  }

  /** Red sparks when player takes zone damage */
  zoneDamageEffect(position) {
    for (let i = 0; i < 3; i++) {
      const geo = _sphereGeoS;
      const mat = new THREE.MeshBasicMaterial({
        color: 0xff2200, transparent: true, opacity: 0.8,
      });
      const m = new THREE.Mesh(geo, mat);
      m.position.copy(position).add(new THREE.Vector3(0, 1, 0));
      this.scene.add(m);
      const life = 0.2 + Math.random() * 0.2;
      this.particles.push({
        mesh: m,
        life,
        maxLife: life,
        vel: new THREE.Vector3(
          (Math.random() - 0.5) * 4,
          Math.random() * 3,
          (Math.random() - 0.5) * 4
        ),
        scale: 0.95,
      });
    }
  }

  _flash(position, color, intensity, duration) {
    const light = new THREE.PointLight(color, intensity, 10);
    light.position.copy(position).add(new THREE.Vector3(0, 1.5, 0));
    this.scene.add(light);
    const fadeInterval = setInterval(() => {
      light.intensity *= 0.85;
      if (light.intensity < 0.05) {
        clearInterval(fadeInterval);
        this.scene.remove(light);
        light.dispose();
      }
    }, 16);
  }

  dispose() {
    for (const p of this.particles) {
      this.scene.remove(p.mesh);
      p.mesh.material.dispose();
    }
    this.particles = [];
  }
}
